import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { useEntitlements } from "@/hooks/useEntitlements";
import PremiumUpsell from "@/components/PremiumUpsell";
import type { Tier } from "@/hooks/useEntitlements";
import {
  GraduationCap,
  Lock,
  Clock,
  BookOpen,
  ChevronRight,
  Sparkles,
  PlayCircle,
  Compass,
  Target,
  Brain,
  Newspaper,
  Star,
  Award,
} from "lucide-react";
import type { Tier } from "@/hooks/useEntitlements";

const TIER_COLORS: Record<Tier, string> = {
  free: "bg-slate-500/10 text-slate-600",
  starter: "bg-emerald-500/10 text-emerald-600",
  execution: "bg-amber-500/10 text-amber-600",
  professional: "bg-violet-500/10 text-violet-600",
  elite: "bg-amber-400/20 text-amber-700",
};

const TIER_LABELS: Record<Tier, string> = {
  free: "Free",
  starter: "Starter",
  execution: "Execution",
  professional: "Professional",
  elite: "Elite",
};

const DIFFICULTY_COLORS: Record<string, string> = {
  beginner: "bg-emerald-500/10 text-emerald-600",
  intermediate: "bg-amber-500/10 text-amber-600",
  advanced: "bg-rose-500/10 text-rose-600",
};

interface CourseWithMeta {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  cover_image_url: string | null;
  difficulty: string | null;
  estimated_hours: number | null;
  required_tier: Tier;
  is_free_preview: boolean;
  is_featured: boolean;
  category: string | null;
  instructor_name: string | null;
  module_count: number;
  lesson_count: number;
  completed_lessons: number;
}

const fadeInUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.06 } },
};

export default function LearningPaths() {
  const { user } = useAuth();
  const { tier, checkAccess } = useEntitlements();
  const navigate = useNavigate();

  const [courses, setCourses] = useState<CourseWithMeta[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);

    const { data: coursesData } = await supabase
      .from("courses")
      .select(
        "id, slug, title, subtitle, description, cover_image_url, difficulty, estimated_hours, required_tier, is_free_preview, is_featured, category, instructor_name"
      )
      .eq("is_published", true)
      .order("sort_order", { ascending: true });

    if (!coursesData) {
      setCourses([]);
      setLoading(false);
      return;
    }

    const courseIds = coursesData.map((c) => c.id);

    const [{ data: mods }, { data: less }, { data: progressData }] = await Promise.all([
      supabase.from("modules").select("id, course_id").eq("is_published", true).in("course_id", courseIds),
      supabase.from("lessons").select("id, module_id").in(
        "module_id",
        (await supabase.from("modules").select("id").eq("is_published", true).in("course_id", courseIds)).data?.map((m) => m.id) ?? []
      ),
      user
        ? supabase
            .from("lesson_progress")
            .select("lesson_id, read_completed_at, video_completed_at")
            .eq("user_id", user.id)
        : { data: [] },
    ]);

    const modCountByCourse = new Map<string, number>();
    const modIdToCourse = new Map<string, string>();
    mods?.forEach((m) => {
      modCountByCourse.set(m.course_id, (modCountByCourse.get(m.course_id) || 0) + 1);
      modIdToCourse.set(m.id, m.course_id);
    });

    const lessonCountByCourse = new Map<string, number>();
    const completedByCourse = new Map<string, number>();
    const progressMap = new Set(
      (progressData ?? []).filter((p) => p.read_completed_at || p.video_completed_at).map((p) => p.lesson_id)
    );

    less?.forEach((l) => {
      const courseId = modIdToCourse.get(l.module_id);
      if (courseId) {
        lessonCountByCourse.set(courseId, (lessonCountByCourse.get(courseId) || 0) + 1);
        if (progressMap.has(l.id)) {
          completedByCourse.set(courseId, (completedByCourse.get(courseId) || 0) + 1);
        }
      }
    });

    const enriched: CourseWithMeta[] = coursesData.map((c) => ({
      ...c,
      required_tier: c.required_tier as Tier,
      module_count: modCountByCourse.get(c.id) || 0,
      lesson_count: lessonCountByCourse.get(c.id) || 0,
      completed_lessons: completedByCourse.get(c.id) || 0,
    }));

    setCourses(enriched);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const featured = courses.filter((c) => c.is_featured).slice(0, 8);
  const startHere = courses.filter((c) => c.difficulty === "beginner" && (c.required_tier === "free" || c.is_free_preview)).slice(0, 6);
  const continueLearning = courses.filter((c) => c.completed_lessons > 0 && c.completed_lessons < c.lesson_count).slice(0, 6);
  const beginnerPath = courses.filter((c) => c.difficulty === "beginner").slice(0, 8);
  const intermediatePath = courses.filter((c) => c.difficulty === "intermediate").slice(0, 8);
  const tradingPsychology = courses.filter((c) => c.category === "Psychology").slice(0, 8);
  const marketBreakdown = courses.filter((c) => c.category === "Market Breakdown").slice(0, 8);
  const recommended = courses.filter((c) => !c.is_featured).slice(0, 8);

  const CourseCard = ({ course }: { course: CourseWithMeta }) => {
    const access = checkAccess(course.required_tier, course.is_free_preview);
    const canAccess = access.canAccess || course.is_free_preview;
    const progressPct = course.lesson_count > 0 ? Math.round((course.completed_lessons / course.lesson_count) * 100) : 0;

    return (
      <Link to={`/course/${course.slug}`} className="shrink-0 w-64 snap-start group" onClick={(e) => { if (!canAccess) e.preventDefault(); }}>
        <div className={`rounded-2xl overflow-hidden border transition-all ${canAccess ? "border-border/40 bg-card/60 hover:bg-card hover:border-primary/20 hover:shadow-sm" : "border-border/30 bg-card/40 opacity-70"}`}>
          <div className="aspect-[16/10] relative overflow-hidden">
            {course.cover_image_url ? (
              <img src={course.cover_image_url} alt={course.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                <BookOpen className="h-8 w-8 text-primary/50" />
              </div>
            )}
            {!canAccess && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <div className="h-8 w-8 rounded-full bg-black/50 flex items-center justify-center">
                  <Lock className="h-4 w-4 text-white" />
                </div>
              </div>
            )}
            {course.is_free_preview && canAccess && (
              <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded-md bg-primary/90 text-white text-[10px] font-medium">Free</div>
            )}
            {progressPct > 0 && progressPct < 100 && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
                <div className="h-full bg-primary" style={{ width: `${progressPct}%` }} />
              </div>
            )}
            {progressPct === 100 && (
              <div className="absolute bottom-2 right-2 h-6 w-6 rounded-full bg-emerald-500/90 flex items-center justify-center">
                <Award className="h-3 w-3 text-white" />
              </div>
            )}
          </div>
          <div className="p-3.5 space-y-2">
            <h4 className="font-display font-medium text-sm leading-snug line-clamp-2">{course.title}</h4>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
              {course.difficulty && <span className={`px-1.5 py-0.5 rounded-md font-medium ${DIFFICULTY_COLORS[course.difficulty]}`}>{course.difficulty}</span>}
              {course.estimated_hours && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{course.estimated_hours}h</span>}
              <span>{course.module_count} modules</span>
            </div>
            {!canAccess && access.upgradeMessage && (
              <p className="text-[11px] text-amber-600 font-medium">{access.upgradeMessage}</p>
            )}
          </div>
        </div>
      </Link>
    );
  };

  const SectionHeader = ({ title, icon: Icon, action }: { title: string; icon: typeof BookOpen; action?: React.ReactNode }) => (
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-display font-semibold text-[15px] flex items-center gap-2">
        <Icon className="h-[18px] w-[18px] text-primary" />
        {title}
      </h3>
      {action}
    </div>
  );

  const HorizontalSection = ({ title, icon, courses: items, action }: { title: string; icon: typeof BookOpen; courses: CourseWithMeta[]; action?: React.ReactNode }) => {
    if (items.length === 0) return null;
    return (
      <motion.div variants={fadeInUp} className="space-y-1">
        <SectionHeader title={title} icon={icon} action={action} />
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x scrollbar-hide">
          {loading
            ? [1, 2, 3, 4].map((i) => <Skeleton key={i} className="shrink-0 w-64 h-40 rounded-2xl" />)
            : items.map((course) => <CourseCard key={course.id} course={course} />)}
        </div>
      </motion.div>
    );
  };

  return (
    <motion.div className="space-y-8 pb-20" variants={staggerContainer} initial="initial" animate="animate">
      {/* Header */}
      <motion.div variants={fadeInUp} className="space-y-4">
        <div className="flex items-start gap-4">
          <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center shrink-0 border border-primary/10">
            <Compass className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-display font-semibold tracking-tight">Learn</h1>
            <p className="text-muted-foreground text-[15px] mt-0.5 max-w-lg">
              Structured education from beginner foundations to professional frameworks. Master the skills that matter.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Your tier:</span>
          <Badge className={`${TIER_COLORS[tier]} font-medium`}>{TIER_LABELS[tier]}</Badge>
          {tier !== "elite" && (
            <Button variant="ghost" size="sm" className="text-primary h-8" onClick={() => navigate("/membership")}>
              <Sparkles className="w-4 h-4 mr-1" /> Upgrade
            </Button>
          )}
        </div>
      </motion.div>

      {/* Continue Learning */}
      {continueLearning.length > 0 && (
        <HorizontalSection
          title="Continue Learning"
          icon={PlayCircle}
          courses={continueLearning}
        />
      )}

      {/* Featured Courses */}
      <HorizontalSection
        title="Featured Courses"
        icon={Star}
        courses={featured}
        action={
          <Link to="/learn" className="text-xs text-primary font-medium flex items-center gap-1 hover:underline">
            View all <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        }
      />

      {/* Start Here */}
      <HorizontalSection
        title="Start Here"
        icon={BookOpen}
        courses={startHere}
      />

      {/* Beginner Path */}
      <HorizontalSection
        title="Beginner Path"
        icon={Target}
        courses={beginnerPath}
      />

      {/* Intermediate Path */}
      <HorizontalSection
        title="Intermediate Path"
        icon={Award}
        courses={intermediatePath}
      />

      {/* Trading Psychology */}
      <HorizontalSection
        title="Trading Psychology"
        icon={Brain}
        courses={tradingPsychology}
      />

      {/* Market Breakdown Series */}
      <HorizontalSection
        title="Market Breakdown Series"
        icon={Newspaper}
        courses={marketBreakdown}
      />

      {/* Recommended */}
      {recommended.length > 0 && (
        <HorizontalSection
          title="Recommended For You"
          icon={Sparkles}
          courses={recommended}
        />
      )}

      {/* Empty state */}
      {!loading && courses.length === 0 && (
        <motion.div variants={fadeInUp}>
          <Card className="p-6 text-center rounded-2xl border-border/50 bg-card/60">
            <GraduationCap className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
            <h3 className="font-display font-semibold text-[15px]">No courses available yet</h3>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-xs mx-auto">Check back soon. Our educators are preparing premium content.</p>
          </Card>
        </motion.div>
      )}

      {/* Premium Upsell */}
      {tier !== "elite" && tier !== "professional" && (
        <motion.div variants={fadeInUp} className="pt-4">
          <PremiumUpsell
            tier={tier === "free" ? "starter" : tier === "starter" ? "execution" : "professional"}
          />
        </motion.div>
      )}
    </motion.div>
  );
}
